/**
 * Settings → API Access
 *
 * Manages Knowledge-API service accounts: create/rename/deactivate accounts,
 * mint/revoke API tokens (raw shown once), and grant per-page doc access.
 *
 * A service account is a `user_type='service'` user, so its page grants reuse
 * the existing user doc-access endpoints (`useUserDocAccess`) + `TreeAccessPicker`
 * verbatim. Access is deny-by-default for the Knowledge API — an account sees
 * only what's explicitly granted here.
 */
import { useState, useEffect, useCallback } from "react";
import {
  KeyRound, Plus, Trash2, ArrowLeft, Copy, Check, ShieldCheck,
  Loader2, AlertCircle, X, Ban, Folder, ChevronRight, Search,
} from "lucide-react";
import { authFetch } from "../utils/authFetch";
import { ConfirmDialog } from "./ConfirmDialog";
import TreeAccessPicker from "./TreeAccessPicker";
import DataSourcesPanel from "./DataSourcesPanel";
import { useUserDocAccess, type AccessRule } from "../hooks/useDocAccess";
import { AudienceChip, type ProjectAudience } from "./audience";

interface ServiceAccount {
  id: string; name: string; email: string; status: string;
  createdAt: string | null; tokenCount: number; projectCount: number;
}
interface ApiToken {
  id: string; prefix: string; name: string | null;
  createdAt: string | null; revokedAt: string | null;
}
interface Grant { projectId: string; slug: string; name: string; rules: number; }
interface NewToken { id: string; rawToken: string; prefix: string; name: string | null; }
interface AdminProject extends ProjectAudience { id: string; name: string; slug: string; visibility: string; isPublished: boolean; }

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

/* ── Copy-to-clipboard button ─────────────────────────────────────────── */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors">
      {copied ? <><Check className="size-3 text-emerald-500" /> Copied</> : <><Copy className="size-3" /> Copy</>}
    </button>
  );
}

/* ── One-time raw-token reveal ────────────────────────────────────────── */
function TokenReveal({ token, onDone }: { token: NewToken; onDone: () => void }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 space-y-2">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-amber-800">
        <AlertCircle className="size-4" /> Copy this token now — it won't be shown again
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 px-3 py-2 rounded-lg bg-white border border-amber-200 text-[12px] font-mono text-slate-800 break-all">{token.rawToken}</code>
        <CopyButton text={token.rawToken} />
      </div>
      <div className="flex justify-end">
        <button onClick={onDone} className="text-[12px] font-medium text-amber-800 hover:text-amber-900">Done</button>
      </div>
    </div>
  );
}

/* ── Create-account modal ─────────────────────────────────────────────── */
function CreateAccountModal({ onClose, onCreated }: { onClose: () => void; onCreated: (token: NewToken) => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true); setErr("");
    try {
      const r = await authFetch("/api/admin/service-accounts", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.detail || "Failed to create"); setBusy(false); return; }
      onCreated(d.token as NewToken);
    } catch { setErr("Network error"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">New service account</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"><X className="size-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Recruiting AI"
              onKeyDown={e => { if (e.key === "Enter") submit(); }}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" autoFocus />
            <p className="text-[11px] text-slate-400 mt-1">A machine principal for another product to read the knowledge base. It can't sign in — only its API token works.</p>
          </div>
          {err && <div className="flex items-center gap-2 text-xs text-red-600"><AlertCircle className="size-3.5" /> {err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
          <button onClick={submit} disabled={busy || !name.trim()} className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Tokens tab ───────────────────────────────────────────────────────── */
function TokensTab({ accountId, onCountChange }: { accountId: string; onCountChange: () => void }) {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [minting, setMinting] = useState(false);
  const [fresh, setFresh] = useState<NewToken | null>(null);
  const [revoke, setRevoke] = useState<ApiToken | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`/api/admin/service-accounts/${accountId}/tokens`);
    if (r.ok) setTokens((await r.json()).tokens);
    setLoading(false);
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  const mint = async () => {
    setMinting(true);
    const r = await authFetch(`/api/admin/service-accounts/${accountId}/tokens`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: null }),
    });
    if (r.ok) { setFresh(await r.json()); await load(); onCountChange(); }
    setMinting(false);
  };

  const doRevoke = async () => {
    if (!revoke) return;
    const r = await authFetch(`/api/admin/service-accounts/${accountId}/tokens/${revoke.id}`, { method: "DELETE" });
    setRevoke(null);
    if (r.ok) { await load(); onCountChange(); }
  };

  return (
    <div className="space-y-4">
      {fresh && <TokenReveal token={fresh} onDone={() => setFresh(null)} />}
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-slate-500">Tokens authenticate the account as <code className="text-[11px] font-mono">Authorization: Bearer &lt;token&gt;</code>. Rotate by minting a new one, then revoking the old.</p>
        <button onClick={mint} disabled={minting} className="shrink-0 ml-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-[12px] font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
          {minting ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />} Mint token
        </button>
      </div>
      {loading ? (
        <div className="py-8 text-center text-sm text-slate-400"><Loader2 className="size-4 animate-spin inline mr-2" />Loading…</div>
      ) : tokens.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">No tokens yet.</div>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
          {tokens.map(t => (
            <div key={t.id} className="flex items-center gap-3 px-4 py-2.5">
              <code className="text-[12px] font-mono text-slate-700">{t.prefix}<span className="text-slate-300">••••••••••••</span></code>
              {t.name && <span className="text-[11px] text-slate-400">{t.name}</span>}
              <span className="text-[11px] text-slate-400 ml-auto">Created {fmtDate(t.createdAt)}</span>
              {t.revokedAt ? (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Revoked</span>
              ) : (
                <button onClick={() => setRevoke(t)} title="Revoke" className="p-1 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors"><Ban className="size-3.5" /></button>
              )}
            </div>
          ))}
        </div>
      )}
      {revoke && (
        <ConfirmDialog title="Revoke token" message={`Requests using ${revoke.prefix}…… will stop working immediately. This can't be undone.`}
          confirmLabel="Revoke" onConfirm={doRevoke} onCancel={() => setRevoke(null)} />
      )}
    </div>
  );
}

/* ── Access tab ───────────────────────────────────────────────────────── */
function AccessTab({ accountId, onSaved }: { accountId: string; onSaved: () => void }) {
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [summary, setSummary] = useState<Record<string, { mode: string; ruleCount: number }>>({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<AdminProject | null>(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<null | "full" | "none">(null);
  const [confirm, setConfirm] = useState<null | "full" | "none">(null);
  const [bulkErr, setBulkErr] = useState("");

  const loadSummary = useCallback(async () => {
    const r = await authFetch(`/api/admin/users/${accountId}/doc-access-summary`);
    if (r.ok) setSummary(await r.json());
  }, [accountId]);

  useEffect(() => {
    (async () => {
      const r = await authFetch("/api/admin/projects");
      // Every project: service accounts are Joveo's own integrations, so a
      // super admin may grant one pages on a Private or Partner project too.
      // There the account reads only what is granted here (the resolver's
      // service rung and build_access_filter).
      if (r.ok) setProjects(await r.json());
      await loadSummary();
      setLoading(false);
    })();
  }, [loadSummary]);

  if (loading) return <div className="py-8 text-center text-sm text-slate-400"><Loader2 className="size-4 animate-spin inline mr-2" />Loading…</div>;

  const enabled = projects.filter(p => (summary[p.id]?.ruleCount ?? 0) > 0).length;
  const ql = q.trim().toLowerCase();
  const shown = ql ? projects.filter(p => p.name.toLowerCase().includes(ql) || p.slug.includes(ql)) : projects;
  const shownIds = shown.map(p => p.id);
  const allShownSelected = shownIds.length > 0 && shownIds.every(id => sel.has(id));

  const toggle = (id: string) => setSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel(prev => {
    const n = new Set(prev);
    if (allShownSelected) shownIds.forEach(id => n.delete(id));
    else shownIds.forEach(id => n.add(id));
    return n;
  });

  // How many of the selected projects already have access configured — i.e. what
  // a bulk action would overwrite.
  const replacingCount = [...sel].filter(id => (summary[id]?.ruleCount ?? 0) > 0).length;


  const bulk = async (access: "full" | "none") => {
    setBulkBusy(access);
    const r = await authFetch(`/api/admin/service-accounts/${accountId}/project-access`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ project_ids: [...sel], access }),
    });
    setBulkBusy(null);
    if (r.ok) {
      setSel(new Set());
      setBulkErr("");
      onSaved();
    } else {
      // Was swallowed: the spinner stopped and nothing happened. The server
      // opens a transaction PER PROJECT, so a refusal part-way leaves the
      // earlier ones committed — reload either way so the panel shows what
      // actually landed.
      const d = await r.json().catch(() => ({}));
      setBulkErr(typeof d.detail === "string" ? d.detail
                 : (d.detail?.error || "Could not update access"));
    }
    await loadSummary();
  };
  const runConfirmed = () => { if (!confirm) return; const a = confirm; setConfirm(null); bulk(a); };

  return (
    <div className="space-y-3">
      {bulkErr && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700">
          {bulkErr}
        </div>
      )}
      <p className="text-[13px] text-slate-500">
        Grant access per project — tick rows to grant/revoke whole projects in bulk, or click a row to pick specific pages. The account sees <span className="font-medium text-slate-600">only</span> what's granted (deny-by-default).
      </p>

      {/* Toolbar: select-all + filter + enabled count */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-[12px] text-slate-600 cursor-pointer select-none">
          <input type="checkbox" checked={allShownSelected} onChange={toggleAll}
            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/20" />
          Select all{ql ? " shown" : ""}
        </label>
        {projects.length > 8 && (
          <div className="relative flex-1 max-w-xs">
            <Search className="size-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter projects…"
              className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
          </div>
        )}
        <span className="ml-auto text-[11px] text-slate-400">{enabled} of {projects.length} enabled</span>
      </div>

      {/* Bulk action bar */}
      {sel.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-200">
          <span className="text-[12px] font-medium text-indigo-700">{sel.size} selected</span>
          {replacingCount > 0 && (
            <span className="text-[11px] text-amber-600">· {replacingCount} already configured</span>
          )}
          <div className="flex-1" />
          <button onClick={() => setConfirm("full")} disabled={!!bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-[12px] font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
            {bulkBusy === "full" ? <Loader2 className="size-3.5 animate-spin" /> : <ShieldCheck className="size-3.5" />} Grant full access
          </button>
          <button onClick={() => setConfirm("none")} disabled={!!bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-slate-600 border border-slate-200 text-[12px] font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors">
            {bulkBusy === "none" ? <Loader2 className="size-3.5 animate-spin" /> : <Ban className="size-3.5" />} Clear access
          </button>
          <button onClick={() => setSel(new Set())} className="text-[12px] font-medium text-slate-400 hover:text-slate-600 px-1">Clear</button>
        </div>
      )}

      <div className="space-y-1">
        {shown.map(p => {
          const rc = summary[p.id]?.ruleCount ?? 0;
          const checked = sel.has(p.id);
          return (
            <div key={p.id}
              className={`flex items-center gap-2 pl-2.5 pr-3 py-2 rounded-lg border transition-colors ${checked ? "border-indigo-300 bg-indigo-50/40" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(p.id)}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/20 shrink-0" />
              <button onClick={() => setOpen(p)} className="flex-1 flex items-center gap-3 min-w-0 text-left">
                <Folder className={`size-4 shrink-0 ${rc > 0 ? "text-indigo-400" : "text-slate-300"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-slate-800 truncate">
                    {p.name}
                    <AudienceChip project={p} className="ml-1.5 align-middle" />
                    {!p.isPublished && <span className="ml-1.5 text-[10px] font-normal text-amber-600">· draft (not served)</span>}
                  </div>
                  <div className="text-[11px] text-slate-400 font-mono truncate">/{p.slug}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {rc > 0 ? (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">{rc} rule{rc !== 1 ? "s" : ""}</span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-400 border border-slate-200">No access</span>
                  )}
                  <ChevronRight className="size-3.5 text-slate-400" />
                </div>
              </button>
            </div>
          );
        })}
      </div>
      {open && (
        <ProjectAccessModal key={open.id} accountId={accountId} project={open}
          onClose={() => { setOpen(null); loadSummary(); onSaved(); }} />
      )}
      {confirm && (
        <ConfirmDialog
          title={confirm === "full" ? "Grant full access" : "Clear access"}
          message={
            confirm === "full"
              ? `Grant full access to ${sel.size} project${sel.size !== 1 ? "s" : ""}.` +
                (replacingCount > 0
                  ? ` ${replacingCount} of them already ${replacingCount === 1 ? "has a custom grant that will be replaced" : "have custom grants that will be replaced"} with the whole project.`
                  : "")
              : `Remove all access from ${sel.size} selected project${sel.size !== 1 ? "s" : ""}.` +
                (replacingCount > 0
                  ? ` ${replacingCount} currently ${replacingCount === 1 ? "has" : "have"} access — this clears it.`
                  : " None of them currently have access.")
          }
          confirmLabel={confirm === "full" ? "Grant full access" : "Clear access"}
          destructive={confirm === "none" || replacingCount > 0}
          onConfirm={runConfirmed}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function ProjectAccessModal({ accountId, project, onClose }: { accountId: string; project: AdminProject; onClose: () => void }) {
  const { config, loading, load, save, clear } = useUserDocAccess(accountId, project.id);
  const [rules, setRules] = useState<AccessRule[]>([]);
  const [saving, setSaving] = useState(false);
  const [internalPaths, setInternalPaths] = useState("");
  const [internalError, setInternalError] = useState("");
  const [syncPaths, setSyncPaths] = useState("");

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setRules(config.rules); }, [config.rules]);
  useEffect(() => {
    authFetch(`/api/admin/service-accounts/${accountId}/internal-grants/${project.id}`)
      .then(r => (r.ok ? r.json() : { paths: [] }))
      .then(d => setInternalPaths((d.paths ?? []).join("\n")))
      .catch(() => setInternalError("Couldn't load internal grants."));
  }, [accountId, project.id]);

  useEffect(() => {
    authFetch(`/api/admin/service-accounts/${accountId}/sync-grants/${project.id}`)
      .then(r => (r.ok ? r.json() : { prefixes: [] }))
      .then(d => setSyncPaths((d.prefixes ?? []).join("\n")))
      .catch(() => setInternalError("Couldn't load write paths."));
  }, [accountId, project.id]);

  const saveSync = async (prefixes: string[]) => {
    const r = await authFetch(`/api/admin/service-accounts/${accountId}/sync-grants/${project.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prefixes }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      setInternalError(typeof d.detail === "string" ? d.detail : "Couldn't save write paths.");
    }
    return r.ok;
  };
  const saveInternal = async (paths: string[]) => {
    const r = await authFetch(`/api/admin/service-accounts/${accountId}/internal-grants/${project.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ paths }),
    });
    if (!r.ok) setInternalError("Couldn't save internal grants.");
    return r.ok;
  };
  const doSave = async () => {
    setSaving(true);
    const paths = internalPaths.split("\n").map(p => p.trim()).filter(Boolean);
    // service accounts = strict allow-list
    const prefixes = syncPaths.split("\n").map(p => p.trim()).filter(Boolean);
    const ok = (await save("override", rules)) && (await saveInternal(paths)) && (await saveSync(prefixes));
    setSaving(false);
    if (ok) onClose();
  };
  const doClear = async () => { await saveInternal([]); await saveSync([]); await clear(); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-800 truncate">{project.name}</h3>
            <p className="text-[11px] text-slate-400 font-mono">/{project.slug}{!project.isPublished && " · draft (not served yet)"}</p>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100 shrink-0"><X className="size-4" /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1">
          <p className="text-[12px] text-slate-500 mb-3">Check the pages/sections this account may read. Leave everything unchecked to deny the whole project.</p>
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400"><Loader2 className="size-4 animate-spin inline mr-2" />Loading tree…</div>
          ) : (
            <TreeAccessPicker projectSlug={project.slug} rules={rules} onChange={setRules} includeSupport />
          )}
          <label htmlFor="internal-grant-paths" className="block text-[12px] font-medium text-slate-700 mt-4 mb-1">Internal content</label>
          <p className="text-[11px] text-slate-500 mb-1.5">Ticks above never open sections marked internal. List an internal section or page here, one path per line, to let this account read it.</p>
          <textarea
            id="internal-grant-paths"
            value={internalPaths}
            onChange={e => setInternalPaths(e.target.value)}
            rows={3}
            placeholder="internal-sales-guidance"
            className="w-full px-3 py-2 text-[12px] font-mono border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <label htmlFor="sync-write-paths" className="block text-[12px] font-medium text-slate-700 mt-4 mb-1">Write paths</label>
          <p className="text-[11px] text-slate-500 mb-1.5">Paths this account may write on its own branches through safe sync, one per line. Inside a section, for example <code>documentation/generated</code>. It can never write to main.</p>
          <textarea
            id="sync-write-paths"
            value={syncPaths}
            onChange={e => setSyncPaths(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-[12px] font-mono border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          {internalError && <p role="alert" className="text-[11px] text-red-600 mt-1">{internalError}</p>}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between shrink-0">
          <button onClick={doClear} className="text-[12px] font-medium text-slate-500 hover:text-red-600">Clear access</button>
          <div className="flex items-center gap-2.5">
            <span className="text-[11px] text-slate-400">{rules.length} rule{rules.length !== 1 ? "s" : ""} selected</span>
            <button onClick={doSave} disabled={saving || loading} className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 text-white text-[13px] font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
              {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Account detail ───────────────────────────────────────────────────── */
function AccountDetail({ accountId, onBack, onChanged }: { accountId: string; onBack: () => void; onChanged: () => void }) {
  const [acct, setAcct] = useState<(ServiceAccount & { tokens: ApiToken[]; grants: Grant[] }) | null>(null);
  const [tab, setTab] = useState<"tokens" | "access">("tokens");
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const load = useCallback(async () => {
    const r = await authFetch(`/api/admin/service-accounts/${accountId}`);
    if (r.ok) setAcct(await r.json());
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  const deactivate = async () => {
    setConfirmDeactivate(false);
    const r = await authFetch(`/api/admin/service-accounts/${accountId}`, { method: "DELETE" });
    if (r.ok) { onChanged(); onBack(); }
  };

  if (!acct) return <div className="py-10 text-center text-sm text-slate-400"><Loader2 className="size-4 animate-spin inline mr-2" />Loading…</div>;

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-700">
        <ArrowLeft className="size-3.5" /> All service accounts
      </button>

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200 flex items-center justify-center shrink-0"><KeyRound className="size-5" /></div>
          <div>
            <h2 className="text-[17px] font-semibold text-slate-800 leading-tight">{acct.name}</h2>
            <span className="text-[11px] font-mono text-slate-400">{acct.email}</span>
          </div>
        </div>
        <button onClick={() => setConfirmDeactivate(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
          <Trash2 className="size-3.5" /> Deactivate
        </button>
      </div>

      <div className="flex items-center gap-0.5 border-b border-slate-200">
        {(["tokens", "access"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-[12px] font-medium border-b-2 transition-colors -mb-px capitalize ${tab === t ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {t === "tokens" ? <KeyRound className="size-3.5" /> : <ShieldCheck className="size-3.5" />}{t}
          </button>
        ))}
      </div>

      {tab === "tokens" && <TokensTab accountId={accountId} onCountChange={() => { load(); onChanged(); }} />}
      {tab === "access" && <AccessTab accountId={accountId} onSaved={() => { load(); onChanged(); }} />}

      {confirmDeactivate && (
        <ConfirmDialog title="Deactivate service account"
          message={`${acct.name} will stop working immediately and all its tokens will be revoked.`}
          confirmLabel="Deactivate" onConfirm={deactivate} onCancel={() => setConfirmDeactivate(false)} />
      )}
    </div>
  );
}

/* ── Panel root ───────────────────────────────────────────────────────── */
export default function ApiAccessPanel() {
  const [accounts, setAccounts] = useState<ServiceAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<NewToken | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch("/api/admin/service-accounts");
    if (r.ok) setAccounts((await r.json()).accounts);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (selectedId) {
    return <AccountDetail accountId={selectedId} onBack={() => setSelectedId(null)} onChanged={load} />;
  }

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-[17px] font-semibold text-slate-800 leading-tight flex items-center gap-2"><KeyRound className="size-5 text-slate-400" /> API Access</h2>
          <p className="text-[13px] text-slate-500 mt-1 max-w-xl">Service accounts let other products read your published content through the Knowledge API (<code className="text-[11px] font-mono">/api/kb</code>). Grant each one per-page access and hand over a token.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors">
          <Plus className="size-3.5" /> New service account
        </button>
      </div>

      {justCreated && <TokenReveal token={justCreated} onDone={() => setJustCreated(null)} />}

      {loading ? (
        <div className="py-12 text-center text-sm text-slate-400"><Loader2 className="size-4 animate-spin inline mr-2" />Loading…</div>
      ) : accounts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-xl border border-dashed border-slate-200">
          <div className="size-12 rounded-2xl bg-indigo-50 flex items-center justify-center mb-3"><KeyRound className="size-6 text-indigo-400" /></div>
          <h3 className="text-sm font-semibold text-slate-700 mb-1">No service accounts yet</h3>
          <p className="text-xs text-slate-400 mb-4 text-center max-w-xs">Create one to let another product consume the knowledge base.</p>
          <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors"><Plus className="size-3.5" /> New service account</button>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
          {accounts.map(a => (
            <button key={a.id} onClick={() => setSelectedId(a.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
              <div className="size-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><KeyRound className="size-4" /></div>
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-slate-800 truncate">{a.name}</div>
                <div className="text-[11px] font-mono text-slate-400 truncate">{a.email}</div>
              </div>
              <div className="ml-auto flex items-center gap-4 text-[11px] text-slate-400">
                <span className="inline-flex items-center gap-1"><KeyRound className="size-3" /> {a.tokenCount}</span>
                <span className="inline-flex items-center gap-1"><ShieldCheck className="size-3" /> {a.projectCount}</span>
                <span>Created {fmtDate(a.createdAt)}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <DataSourcesPanel />

      {showCreate && (
        <CreateAccountModal onClose={() => setShowCreate(false)}
          onCreated={(token) => { setShowCreate(false); setJustCreated(token); load(); }} />
      )}
    </div>
  );
}
